/**
 * Phase 3 — Hindrance Register (Iter 200).
 * Per-project delay log with responsibility attribution + EOT-claim rollup
 * (total days lost by Client / Contractor / Subcontractor / Other).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Plus, CheckCircle2, Filter } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import { formatDate as fmt } from '../../lib/format';

const RESP = ['Client', 'Contractor', 'Subcontractor', 'Other'];
const STATUSES = ['open', 'in_progress', 'resolved', 'waived'];
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  waived: 'bg-slate-100 text-slate-600 border-slate-200',
};
const RESP_TONE = {
  Client: 'bg-blue-50 text-blue-700 border-blue-200',
  Contractor: 'bg-purple-50 text-purple-700 border-purple-200',
  Subcontractor: 'bg-orange-50 text-orange-700 border-orange-200',
  Other: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function HindranceRegister() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fStatus, setFStatus] = useState('');
  const [fResp, setFResp] = useState('');
  const [fMinDays, setFMinDays] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', responsibility: 'Client', impact_days: '', expected_resolution_date: '' });

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => { if (!projectId && projects.length) setProjectId(projects[0].id); }, [projects, projectId]);

  const load = () => {
    if (!projectId) return;
    setLoading(true);
    const q = new URLSearchParams({ project_id: projectId });
    if (fStatus) q.set('status', fStatus);
    if (fResp) q.set('responsibility', fResp);
    if (fMinDays) q.set('min_impact_days', fMinDays);
    apiGet(`/hindrances?${q}`).then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(load, [projectId, fStatus, fResp, fMinDays]); // eslint-disable-line

  const submit = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    apiPost('/hindrances', { ...form, project_id: projectId, impact_days: Number(form.impact_days || 0) })
      .then(() => { toast.success('Hindrance logged'); setOpen(false); setForm({ title: '', description: '', responsibility: 'Client', impact_days: '', expected_resolution_date: '' }); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to create'));
  };

  const resolve = (h) => {
    apiPost(`/hindrances/${h.id}/resolve`, { status: 'resolved' })
      .then(() => { toast.success('Marked resolved'); load(); })
      .catch(() => toast.error('Failed to resolve'));
  };

  const rollup = data?.days_lost_by_party || {};
  const rows = data?.hindrances || [];
  const maxDays = useMemo(() => Math.max(1, ...RESP.map(r => rollup[r] || 0)), [rollup]);

  return (
    <div>
      <PageHeader overline="Project Execution" title="Hindrance Register" subtitle="Delay log with responsibility attribution — the backbone of EOT claims" />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="hindrance-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[280px]">
            {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
        <div className="ml-auto">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="hindrance-add-btn">
                <Plus size={14} /> Log Hindrance
              </button>
            </DialogTrigger>
            <DialogContent data-testid="hindrance-dialog">
              <DialogHeader><DialogTitle>Log a Hindrance</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Drawing approval delayed by consultant)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="hindrance-title-input" />
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description / context" rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Responsibility</label>
                    <select value={form.responsibility} onChange={e => setForm({ ...form, responsibility: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="hindrance-resp-select">
                      {RESP.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Impact (days)</label>
                    <input type="number" value={form.impact_days} onChange={e => setForm({ ...form, impact_days: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="hindrance-impact-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Expected resolution date</label>
                  <input type="date" value={form.expected_resolution_date} onChange={e => setForm({ ...form, expected_resolution_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
              </div>
              <DialogFooter>
                <button onClick={submit} className="px-4 py-2 text-sm font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="hindrance-submit-btn">Save</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Rollup: days lost by party */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="hindrance-rollup">
        {RESP.map(r => (
          <div key={r} className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">{r}</div>
            <div className="text-2xl font-display font-extrabold tabular-nums">{(rollup[r] || 0).toFixed(1)}<span className="text-xs font-normal text-slate-400 ml-1">days lost</span></div>
            <div className="mt-2 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
              <div className={`h-full ${RESP_TONE[r].split(' ')[1].replace('text', 'bg')}`} style={{ width: `${((rollup[r] || 0) / maxDays) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
        <Filter size={14} className="text-slate-400" />
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="hindrance-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={fResp} onChange={e => setFResp(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="hindrance-filter-resp">
          <option value="">All parties</option>
          {RESP.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input type="number" value={fMinDays} onChange={e => setFMinDays(e.target.value)} placeholder="Min impact days" className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white w-32" data-testid="hindrance-filter-mindays" />
        <span className="text-xs text-slate-500 ml-2">Total: <b>{(data?.total_days_lost || 0).toFixed(1)}</b> days lost · {data?.open_count || 0} open</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="hindrance-table">
            <thead><tr><th>Title</th><th>Responsibility</th><th className="text-right">Impact</th><th>Raised</th><th>Expected</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-xs text-slate-400 py-10">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10">No hindrances logged. Click "Log Hindrance" to add one.</td></tr>
              ) : rows.map(h => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td>
                    <div className="font-display font-semibold text-[13px] text-[#0F172A]">{h.title}</div>
                    {h.description && <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{h.description}</div>}
                  </td>
                  <td><span className={`tag border ${RESP_TONE[h.responsibility]}`}>{h.responsibility}</span></td>
                  <td className="text-right font-display font-bold tabular-nums">{(h.impact_days || 0).toFixed(1)}d</td>
                  <td className="text-xs text-slate-600">{fmt(h.raised_at)}</td>
                  <td className="text-xs text-slate-600">{fmt(h.expected_resolution_date)}</td>
                  <td><span className={`tag border ${STATUS_TONE[h.status]}`}>{h.status.replace('_', ' ')}</span></td>
                  <td className="text-right">
                    {(h.status === 'open' || h.status === 'in_progress') && (
                      <button onClick={() => resolve(h)} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`hindrance-resolve-${h.id}`}>
                        <CheckCircle2 size={13} /> Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
